import { Layers } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '../Popover/Popover'

export interface VendorCostBreakdownEntry {
  lot_name?: string | null
  vendor_id?: number | null
  vendor_name: string | null
  qty: number
  /** Per-lot unit cost (detail/history-row shape). */
  unit_cost?: number | null
  /** Per-vendor qty-weighted unit cost (aggregated bucket shape — see
   *  margin_report.py's `_aggregate_rows`). Either this or `unit_cost`
   *  is present depending on which endpoint produced the row; never both. */
  blended_unit_cost?: number | null
  cost_source?: string
  mixed_vendor_lot?: boolean
  /** Explains why this line fell back to average cost instead of the
   *  vendor's specific PO price — set when the PO line's UoM and the
   *  delivery's UoM were confirmed to differ (e.g. "PO line priced per
   *  lb, delivery recorded in kg — using average cost instead of the
   *  vendor's PO price"). A mismatch is never guessed across as a
   *  conversion; it's a reason to distrust the specific vendor price. */
  conversion_note?: string | null
}

export interface VendorCostBreakdownProps {
  breakdown: VendorCostBreakdownEntry[] | null | undefined
  blendedUnitCost?: number | null
  className?: string
}

function formatCost(value: number | null | undefined): string {
  if (value == null) return '—'
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })
}

/**
 * Per-vendor/per-lot cost breakdown for a lot-tracked product's shipment —
 * the blended $/unit is what's visible in the cell, with a popover
 * drill-down into which vendor/lot supplied each portion. Additive to (and
 * separate from) the True Margin engine's average-cost COGS; see
 * app/services/lot_vendor_cost.py. Shared between the Margins and Product
 * x Customer Pricing modules, which both surface this same data.
 */
export function VendorCostBreakdown({ breakdown, blendedUnitCost, className }: VendorCostBreakdownProps) {
  if (!breakdown || breakdown.length === 0) {
    return <span className="text-tertiary">—</span>
  }

  const vendorCount = new Set(breakdown.map((row) => row.vendor_name).filter(Boolean)).size

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 text-sm text-primary hover:text-accent-text ${className ?? ''}`}
        >
          <span>{formatCost(blendedUnitCost)}</span>
          {vendorCount > 1 ? (
            <Layers size={12} className="text-tertiary" aria-label={`${vendorCount} vendors`} />
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <div className="text-role-section-label px-1 pb-1">Vendor cost breakdown</div>
        <div className="flex flex-col gap-1">
          {breakdown.map((row, i) => (
            <div key={i} className="px-1">
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate text-role-data-secondary">
                  {row.vendor_name ?? 'Unknown vendor'}
                  {row.lot_name ? ` · ${row.lot_name}` : ''}
                  {row.mixed_vendor_lot ? ' (mixed)' : ''}
                </span>
                <span className="flex-shrink-0 text-role-metadata">
                  {row.qty.toLocaleString()} @ {formatCost(row.unit_cost ?? row.blended_unit_cost)}
                </span>
              </div>
              {row.conversion_note ? (
                <div className="text-role-metadata truncate">{row.conversion_note}</div>
              ) : null}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
